import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Breadcrumb, Card, Button, Tag, Typography, Row, Col, Space, Descriptions, message } from 'antd';
import * as Icon from 'react-feather';
import {
  communityActionStarts,
  communityActionErrors,
  getCommunityByIdAction,
  joinCommunityAction,
  leaveCommunityAction,
} from '../../core/data/redux/modules/community/actions';
import { getCommunityById, joinCommunity, leaveCommunity } from '../../services/communityServices';

const { Title, Text, Paragraph } = Typography;

const CommunityDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { currentCommunity, loading, error } = useSelector((state: any) => state.community);

  useEffect(() => {
    if (id) {
      fetchCommunity();
    }
  }, [id]);

  const fetchCommunity = async () => {
    dispatch(communityActionStarts());
    try {
      const res = await getCommunityById(id!);
      if (res.error === true) throw new Error(res.message || 'Error fetching community');
      dispatch(getCommunityByIdAction(res.data));
    } catch (err: any) {
      dispatch(communityActionErrors(err.message));
      message.error(err.message);
    }
  };

  const handleJoin = async () => {
    try {
      await joinCommunity(id!);
      dispatch(joinCommunityAction(id!));
      message.success('Joined community successfully');
      fetchCommunity();
    } catch (err: any) {
      message.error(err.message);
    }
  };

  const handleLeave = async () => {
    try {
      await leaveCommunity(id!);
      dispatch(leaveCommunityAction(id!));
      message.success('Left community successfully');
      fetchCommunity();
    } catch (err: any) {
      message.error(err.message);
    }
  };

  if (loading) return <Card loading style={{ margin: 24 }} />;
  if (error) return <Card style={{ margin: 24 }}><Text type="danger">{error}</Text></Card>;
  if (!currentCommunity) return <Card style={{ margin: 24 }}><Text>Community not found</Text></Card>;

  return (
    <div className="page-wrapper page-settings" style={{ paddingTop: 0 }}>
      <div className="content">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <Link to="/admin/community-management">Community Management</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Community Detail</Breadcrumb.Item>
        </Breadcrumb>
        
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <Title level={4} style={{ margin: 0 }}>Community Details</Title>
            <Space>
              <Link to={`/admin/community-management/edit-community/${id}`}>
                <Button icon={<Icon.Edit size={16} />}>Edit</Button>
              </Link>
              {currentCommunity.is_member ? (
                <Button icon={<Icon.LogOut size={16} />} onClick={handleLeave}>
                  Leave Community
                </Button>
              ) : (
                <Button type="primary" icon={<Icon.UserPlus size={16} />} onClick={handleJoin}>
                  Join Community
                </Button>
              )}
            </Space>
          </div>

          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card title="Basic Information" size="small">
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Name">{currentCommunity.name}</Descriptions.Item>
                  <Descriptions.Item label="Unique Name">{currentCommunity.unique_name}</Descriptions.Item>
                  <Descriptions.Item label="ID">{currentCommunity.id}</Descriptions.Item>
                  <Descriptions.Item label="Privacy">
                    <Tag color={currentCommunity.is_private ? 'red' : 'green'}>
                      {currentCommunity.is_private ? 'Private' : 'Public'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Owner">
                    {currentCommunity.owner?.username || 'Unknown'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Member Count">
                    {currentCommunity.member_count || 0}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            {currentCommunity.guideline && (
              <Col span={24}>
                <Card title="Guidelines" size="small">
                  <Paragraph>{currentCommunity.guideline}</Paragraph>
                </Card>
              </Col>
            )}

            <Col span={24}>
              <Card title="Timestamps" size="small">
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Created At">
                    {new Date(currentCommunity.created_at).toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Updated At">
                    {new Date(currentCommunity.updated_at).toLocaleString()}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default CommunityDetail;
